import { useCallback, useEffect, useRef, useState } from 'react'
import QRCode from 'react-qr-code'

import { Button } from 'components/Button'
import { adCodeStrings } from 'constants/strings'

/** 印刷しても読めるように大きめに書き出す。拡大縮小で潰れると読めなくなるため。 */
const PNG_SIZE = 1024
/** 誤り訂正レベル M。多少の汚れ・かすれに耐える。 */
const ERROR_CORRECTION = 'M' as const
/** QRの周囲に必要な余白（モジュール数）。規格上4以上が要る。 */
const QUIET_ZONE_MODULES = 4

type Props = {
  /** QRに入れる値。広告コードの発行URL。 */
  value: string
  /** 画面に表示する一辺のpx。 */
  size?: number
  /** PNGのファイル名に使う。 */
  fileName: string
  className?: string
}

/**
 * 広告コードのQRコードを表示し、PNGとして保存できるようにする。
 *
 * 表示はSVGで行い、保存時だけcanvasへ描き直してPNGにする。
 * 印刷物での利用を想定して1024pxで書き出す。
 */
export function AdCodeQr({ value, size = 160, fileName, className }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [saving, setSaving] = useState(false)
  // 画像化は非同期なので、アンマウント後に state を触らないようにする。
  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const handleSave = useCallback(() => {
    const svg = containerRef.current?.querySelector('svg')
    if (!svg) return
    setSaving(true)

    // react-qr-code の viewBox は QR 本体だけで余白を含まない。
    // 余白（quiet zone）が無いと印刷物で読み取れなくなるため、
    // canvas 側で 4 モジュール分の白余白を足して書き出す。
    const viewBox = svg.getAttribute('viewBox')?.split(/\s+/) ?? []
    const modules = Number(viewBox[2])
    const hasModules = Number.isFinite(modules) && modules > 0
    const totalModules = hasModules
      ? modules + QUIET_ZONE_MODULES * 2
      : 1
    const scale = PNG_SIZE / totalModules
    const inset = hasModules ? QUIET_ZONE_MODULES * scale : 0
    const drawSize = hasModules ? modules * scale : PNG_SIZE

    const source = new XMLSerializer().serializeToString(svg)
    const image = new Image()
    const finish = () => {
      if (!cancelledRef.current) setSaving(false)
    }

    image.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = PNG_SIZE
      canvas.height = PNG_SIZE
      const context = canvas.getContext('2d')
      if (!context) {
        finish()
        return
      }
      // 透過のまま保存すると印刷時に背景が黒く出ることがあるため白で塗る。
      // この白がそのまま quiet zone にもなる。
      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, PNG_SIZE, PNG_SIZE)
      context.drawImage(image, inset, inset, drawSize, drawSize)
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob)
          const anchor = document.createElement('a')
          anchor.href = url
          anchor.download = `${fileName}.png`
          anchor.click()
          // クリック直後に revoke するとダウンロードが始まらない環境があるため
          // 次のタスクまで待ってから解放する。
          window.setTimeout(() => URL.revokeObjectURL(url), 0)
        }
        finish()
      }, 'image/png')
    }
    // 画像化に失敗しても操作不能にならないよう、必ずボタンを戻す。
    image.onerror = finish
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`
  }, [fileName])

  return (
    <div className={className}>
      <div
        ref={containerRef}
        className="inline-flex bg-white p-2"
        data-testid="ad-code-qr"
      >
        <QRCode
          value={value}
          size={size}
          level={ERROR_CORRECTION}
          data-testid="ad-code-qr-svg"
          // quiet zone は QRCode 側の viewBox に含まれないため、外側の padding で確保する
          style={{ height: size, width: size }}
        />
      </div>
      <div className="mt-2">
        <Button size="sm" variant="secondary" onClick={handleSave} disabled={saving}>
          {adCodeStrings.detail.saveQr}
        </Button>
      </div>
    </div>
  )
}

export { QUIET_ZONE_MODULES, PNG_SIZE, ERROR_CORRECTION }
